import {
    Headset,
    Code2,
    Network,
    Cloud,
    Shield,
    Database,
    BarChart3,
    MessageCircle,
} from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "./common";
import { wa, TI_IMAGES } from "../lib/site";
import Ti3D from "./Ti3D";

const icons = [Headset, Code2, Network, Cloud, Shield, Database, BarChart3];

export default function TiSection() {
    const { lang, t } = useLang();
    const msg =
        lang === "pt"
            ? "Olá! Vim pelo site da ANG e gostaria de saber mais sobre os serviços de T.I (Tecnologia da Informação)."
            : "Hi! I came from the ANG website and would like to know more about the IT services.";

    return (
        <section
            id="ti"
            data-testid="ti-section"
            className="relative py-24 md:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_30%,rgba(0,240,255,0.06),transparent_55%)] pointer-events-none" />
            <p className="pointer-events-none select-none absolute top-6 right-0 font-display font-extrabold text-[24vw] leading-none text-outline-faint opacity-30">
                T.I
            </p>

            <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-14">
                    <Reveal>
                        <Eyebrow>
                            <span className="text-cyan-300">{t.ti.eyebrow}</span>
                        </Eyebrow>
                        <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            {t.ti.title}
                        </h2>
                        <p className="mt-4 max-w-xl text-slate-400">{t.ti.sub}</p>
                    </Reveal>
                </div>

                <div className="mb-12">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">
                        {t.ti.photosLabel}
                    </p>
                    <div className="grid sm:grid-cols-3 gap-5">
                        {TI_IMAGES.map((img, i) => (
                            <Reveal key={img.src} delay={i * 0.1}>
                                <figure
                                    data-testid={`ti-photo-${i + 1}`}
                                    className="relative h-44 md:h-52 overflow-hidden rounded-2xl border border-cyan-400/20"
                                >
                                    <img
                                        src={img.src}
                                        alt={t.ti.photos[i]}
                                        loading="lazy"
                                        className="w-full h-full object-cover saturate-[0.55] contrast-[1.05] hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-cyan-400/10 mix-blend-overlay pointer-events-none" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/75 via-transparent to-transparent pointer-events-none" />
                                    <figcaption className="absolute bottom-3 left-3 bg-obsidian/85 backdrop-blur border border-cyan-400/25 rounded-lg px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-cyan-200">
                                        {t.ti.photos[i]}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {t.ti.cards.map((card, i) => {
                        const Icon = icons[i];
                        return (
                            <Reveal key={card.title} delay={i * 0.08} className="h-full">
                                <article
                                    data-testid={`ti-card-${i + 1}`}
                                    className="h-full flex flex-col rounded-2xl border border-cyan-400/20 bg-surface/60 backdrop-blur-sm p-6 md:p-7 hover:border-cyan-400/50 hover:-translate-y-1.5 transition-all"
                                >
                                    <span className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-cyan-300" />
                                    </span>
                                    <h3 className="mt-5 font-display font-bold text-lg leading-snug">
                                        {card.title}
                                    </h3>
                                    <p className="mt-3 text-[13px] text-slate-400 leading-relaxed flex-1">
                                        {card.desc}
                                    </p>
                                    <span className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/70">
                                        ANG · T.I
                                    </span>
                                </article>
                            </Reveal>
                        );
                    })}

                    <Reveal delay={0.3} className="h-full">
                        <div
                            data-testid="ti-card-8"
                            className="h-full flex flex-col justify-between gap-6 rounded-2xl border border-volt/40 bg-volt/5 p-6 md:p-7"
                        >
                            <div>
                                <span className="w-11 h-11 rounded-xl bg-volt/10 border border-volt/30 flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-volt" />
                                </span>
                                <h3 className="mt-5 font-display font-bold text-lg leading-snug">
                                    {t.ti.ctaCard.title}
                                </h3>
                                <p className="mt-3 text-[13px] text-slate-400 leading-relaxed">
                                    {t.ti.ctaCard.desc}
                                </p>
                            </div>
                            <a
                                data-testid="ti-whatsapp-cta"
                                href={wa(msg)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2.5 bg-volt text-obsidian font-bold px-6 py-3.5 rounded-full text-sm hover:shadow-[0_0_36px_rgba(255,230,0,0.45)] hover:-translate-y-0.5 transition-all"
                            >
                                <MessageCircle className="w-4.5 h-4.5" />
                                {t.ti.ctaCard.btn}
                            </a>
                        </div>
                    </Reveal>
                </div>

                <Ti3D />
            </div>
        </section>
    );
}
